import type { ReactNode } from 'react';
import { Badge } from '../../atoms/Badge/Badge';
import { Button } from '../../atoms/Button/Button';
import type { ChartRow } from '../ChartFrame/chartProps';
import styles from './ChartTooltip.module.css';

/**
 * The hover readout for a chart.
 *
 * **Not in Figma.** The file designs the whole chart chrome — axes, gridlines,
 * legend, axis titles — but no tooltip, even though hover is the primary way
 * anyone reads a chart. Its surface is therefore derived from `Tooltip`
 * (99:4758): the same `surface/primary` bubble, `space/8` padding, `radius/md`
 * and `shadow/lg`. Only the *contents* are new, because a chart tooltip lists
 * series and values where the atom holds a sentence. See plan.md §7 item 132 —
 * this wants a real design.
 *
 * The `Tooltip` atom is not reused directly: Recharts positions this itself and
 * the atom owns its own arrow, placement and entrance animation, none of which
 * apply here.
 *
 * **Positioned by `useAnchoredTooltip`, not by Recharts** (2026-09-24). It was
 * passed to Recharts as `content={<ChartTooltip />}`, which cloned it with the
 * props below and moved it with the pointer; it is now rendered into
 * `ChartFrame`'s `hover` slot, pinned to the datum, and the props arrive from
 * the caller instead of from a clone. They stay optional all the same:
 * `CalendarHeatmap` and `WordCloud` have always built their own payload, and
 * nothing here may assume a full set.
 *
 * The bubble can now carry an **action** — the detail behind the point it
 * describes. That is the whole reason the readout had to stop following the
 * pointer: a control inside a surface that moves at the pointer's own speed can
 * never be clicked. See `useAnchoredTooltip` for the mechanics and `action`
 * below for what it costs the design.
 */
export type ChartTooltipEntry = {
  /** Series label — Recharts' `name`. */
  name?: string | number;
  /** Value at the hovered point. */
  value?: string | number;
  /** Series colour, which Recharts echoes back from the mark's `stroke`/`fill`. */
  color?: string;
  dataKey?: string | number;
  /**
   * The whole source row for the hovered point, which Recharts passes alongside
   * the single value. `detail` reads it to find a figure that sits beside the
   * plotted one — the count behind a share, say, which the plot does not carry.
   */
  payload?: ChartRow;
};

/**
 * The comparison period's reading for one series at the hovered point — the
 * figure before, already formatted, and the change from it.
 *
 * `tone` is the caller's to decide, because whether a rise is good depends on
 * the metric (more mentions, yes; more complaints, no) and the tooltip cannot
 * know which it is drawing.
 */
export type ChartTooltipComparison = {
  /** The previous period's value, formatted the same way as the reading. */
  previous: string;
  /** The change, formatted — "+12.7%", "−1.3 pts". Omitted, no badge is drawn. */
  change?: string;
  tone?: 'positive' | 'negative' | 'neutral';
};

export type ChartTooltipProps = {
  /** Recharts only paints the tooltip while this is true. */
  active?: boolean;
  /** One entry per series at the hovered position. */
  payload?: ChartTooltipEntry[];
  /** The x-axis value being hovered. */
  label?: string | number;
  /** Formats each value — thousands separators, currency, percentages. */
  formatter?: (value: string | number | undefined, entry: ChartTooltipEntry) => string;
  /** Formats the heading. */
  labelFormatter?: (label: string | number | undefined) => string;
  /** Hide the heading — right for a pie, where the slice name is the series. */
  hideLabel?: boolean;
  /**
   * Read each value as its **share of the hovered point's total** rather than on
   * its own, and print that percentage as the reading.
   *
   * For a plot drawn with Recharts' `expand` stack offset, where the bar shows a
   * proportion but the payload still carries the raw figure it was computed
   * from: formatting that raw figure as a percentage is how a stack of 13, 15
   * and 20 mentions came to read "1300%, 1500%, 2000%" (2026-09-23). The share
   * has to be divided out here, because the total is only known once every
   * series at that point is in hand, which is inside this component and nowhere
   * else.
   *
   * `formatter` then lands on the count instead, so the row reads "27% · 13" —
   * the proportion the reader came for and the number it stands on.
   */
  share?: boolean;
  /**
   * Opens the detail behind the anchored point — the conversations a slice or a
   * date stands on. Omitted, the readout is a pure readout and no row is drawn.
   *
   * **The `Buttons` atom on `Hierarchy=Ghost grey`** (86:2267), not a control
   * invented here. The first pass drew an underlined text link, which was a
   * seventh button style in a system that has six — corrected on the user's
   * instruction, 2026-09-24.
   *
   * The *placement* is still underived: Figma draws no chart tooltip at all, so
   * the rule above the action, and the fact a readout carries one at all, are
   * this file's own. plan.md §7 item 132 already wanted a designer here.
   */
  action?: () => void;
  /** What the action reads. */
  actionLabel?: ReactNode;
  /**
   * True once the readout has locked and accepts pointer events.
   *
   * The row is drawn either way — an action that appears only after a dwell is
   * an action nobody knows to wait for — but until the lock it sits on a surface
   * with `pointer-events: none` and genuinely cannot be pressed. So it is
   * `disabled` until then: the honest encoding, the one that keeps it out of the
   * tab order, and the one that tells assistive tech what the grey tells the eye.
   */
  locked?: boolean;
  /**
   * A second, quieter figure after the reading. Given the whole entry, so it can
   * reach the source row for something the plot does not draw.
   *
   * Returning `undefined` prints nothing, which is what a row with no companion
   * figure wants — a blank is better than a dash nobody can read.
   */
  detail?: (entry: ChartTooltipEntry) => string | undefined;
  /**
   * The comparison period's reading for this row, drawn after the value as
   * "vs 1,100" and a change badge. Returning `undefined` — comparison off, or no
   * matching bucket in the prior period — leaves the row exactly as it was.
   *
   * A slot of its own rather than another use of `detail`, because `detail` and
   * `share` already contend for the single aside.
   */
  compare?: (entry: ChartTooltipEntry) => ChartTooltipComparison | undefined;
};

const badgeType = { positive: 'positive', negative: 'negative', neutral: 'gray' } as const;

/** A share of the whole, rounded the same way a normalised axis labels itself. */
const percentOfTotal = (value: number, total: number) => `${Math.round((value / total) * 100)}%`;

export function ChartTooltip({
  active,
  payload,
  label,
  formatter,
  labelFormatter,
  hideLabel = false,
  share = false,
  detail,
  compare,
  action,
  actionLabel = 'View details',
  locked = false,
}: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;

  const heading = labelFormatter ? labelFormatter(label) : label;

  /*
   * Magnitudes, so a diverging stack — whose left series is stored negative —
   * totals what it draws rather than cancelling itself towards zero.
   */
  const total = share
    ? payload.reduce((sum, entry) => {
        const value = Number(entry.value);
        return sum + (Number.isFinite(value) ? Math.abs(value) : 0);
      }, 0)
    : 0;

  /** The caller's rendering of one value, or the value as it came. */
  function formatted(entry: ChartTooltipEntry) {
    return formatter ? formatter(entry.value, entry) : entry.value;
  }

  return (
    <div className={styles.bubble} data-locked={locked || undefined}>
      {!hideLabel && heading !== undefined && heading !== '' && (
        <p className={styles.heading}>{heading}</p>
      )}

      <ul className={styles.rows}>
        {payload.map((entry, i) => {
          /*
           * In `share` mode the percentage is the reading and the caller's own
           * formatting becomes the aside. A point whose series all read zero has
           * no share to report, so the raw figure stands alone there rather than
           * claiming "0%" of nothing.
           */
          const showShare = share && total > 0;
          const reading = showShare ? percentOfTotal(Number(entry.value), total) : formatted(entry);
          const aside = showShare ? formatted(entry) : detail?.(entry);
          const before = compare?.(entry);

          return (
            <li key={entry.dataKey ?? entry.name ?? i} className={styles.row}>
              <span
                className={styles.swatch}
                style={{ background: entry.color }}
                aria-hidden="true"
              />
              <span className={styles.name}>{entry.name}</span>
              <span className={styles.value}>
                {reading}
                {aside === undefined || aside === '' ? null : (
                  <span className={styles.detail}>{aside}</span>
                )}
              </span>
              {before && (
                <span className={styles.compare}>
                  <span className={styles.previous}>vs {before.previous}</span>
                  {before.change !== undefined && (
                    <Badge type={badgeType[before.tone ?? 'neutral']}>{before.change}</Badge>
                  )}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {action && (
        /*
         * In the DOM from the first frame, so the readout never changes height
         * under a reader already looking at it — only the button's state changes.
         */
        <div className={styles.action}>
          <Button hierarchy="ghostGrey" onClick={action} disabled={!locked}>
            {actionLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
